import type {
  RegisteredWebhookEndpoint,
  WebhookEndpointView,
  WebhookEventType,
} from "@developerz.ai/ggcoin-sdk";
import { createSignal, For, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { formatDateTime } from "../lib/format.ts";

const EVENTS: readonly WebhookEventType[] = ["transaction.posted", "transaction.reversed"];

export function Webhooks() {
  const { client } = useAuth();
  const [endpoints, setEndpoints] = createSignal<WebhookEndpointView[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);

  const [url, setUrl] = createSignal("");
  const [label, setLabel] = createSignal("");
  const [events, setEvents] = createSignal<Set<WebhookEventType>>(new Set(EVENTS));
  const [busy, setBusy] = createSignal(false);
  // The signing secret is returned exactly once — surface it until dismissed.
  const [registered, setRegistered] = createSignal<RegisteredWebhookEndpoint | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setEndpoints(await client.listWebhookEndpoints());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void refresh();
  });

  function toggleEvent(event: WebhookEventType) {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return next;
    });
  }

  async function onRegister(e: Event) {
    e.preventDefault();
    if (events().size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const ep = await client.registerWebhookEndpoint({
        url: url(),
        label: label(),
        events: [...events()],
      });
      setRegistered(ep);
      setUrl("");
      setLabel("");
      setEvents(new Set(EVENTS));
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onToggleStatus(ep: WebhookEndpointView) {
    setError(null);
    try {
      await client.setWebhookEndpointStatus(ep.id, {
        status: ep.status === "active" ? "disabled" : "active",
      });
      await refresh();
    } catch (err) {
      setError(err);
    }
  }

  async function onDelete(id: string) {
    setError(null);
    try {
      await client.deleteWebhookEndpoint(id);
      await refresh();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <section>
      <h1>Webhooks</h1>
      <p class="muted">
        Outbound endpoints we POST signed events to. The signing secret is shown once.
      </p>
      <ErrorBanner error={error()} />

      <Show when={registered()}>
        {(ep) => (
          <div class="callout" role="status">
            <strong>Copy your signing secret now — it won't be shown again.</strong>
            <pre class="secret">{ep().secret}</pre>
            <button type="button" class="ghost" onClick={() => setRegistered(null)}>
              Done
            </button>
          </div>
        )}
      </Show>

      <form class="card form" onSubmit={onRegister}>
        <h3>Register an endpoint</h3>
        <label for="url">HTTPS URL</label>
        <input
          id="url"
          type="url"
          value={url()}
          placeholder="https://hooks.example.com/ggcoin"
          onInput={(e) => setUrl(e.currentTarget.value)}
          required
        />
        <label for="wh-label">Label</label>
        <input
          id="wh-label"
          value={label()}
          maxLength={80}
          placeholder="prod"
          onInput={(e) => setLabel(e.currentTarget.value)}
          required
        />
        <span class="muted">Events</span>
        <div class="checks">
          <For each={EVENTS}>
            {(event) => (
              <label class="check">
                <input
                  type="checkbox"
                  checked={events().has(event)}
                  onChange={() => toggleEvent(event)}
                />
                {event}
              </label>
            )}
          </For>
        </div>
        <div class="row">
          <button type="submit" class="primary" disabled={busy() || events().size === 0}>
            {busy() ? "Registering…" : "Register endpoint"}
          </button>
        </div>
      </form>

      <div class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th>Label</th>
              <th>URL</th>
              <th>Events</th>
              <th>Status</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <Show
              when={!loading()}
              fallback={
                <tr>
                  <td colSpan={6} class="muted">
                    Loading…
                  </td>
                </tr>
              }
            >
              <Show
                when={endpoints().length > 0}
                fallback={
                  <tr>
                    <td colSpan={6} class="muted">
                      No endpoints yet.
                    </td>
                  </tr>
                }
              >
                <For each={endpoints()}>
                  {(ep) => (
                    <tr>
                      <td>{ep.label}</td>
                      <td class="url-cell">
                        <code>{ep.url}</code>
                      </td>
                      <td>{ep.events.join(", ")}</td>
                      <td>
                        <span class={`badge badge-${ep.status}`}>{ep.status}</span>
                      </td>
                      <td>{formatDateTime(ep.createdAt)}</td>
                      <td class="actions">
                        <button type="button" class="ghost" onClick={() => onToggleStatus(ep)}>
                          {ep.status === "active" ? "Disable" : "Enable"}
                        </button>
                        <button type="button" class="ghost danger" onClick={() => onDelete(ep.id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>
    </section>
  );
}
